import { useEffect, useState } from "react";
import { ApiError, api, type Progress as ProgressData, type ProgressState } from "./api";
import { PageHeader } from "./layout/Layout";
import { Alert, Badge, Box, Loading, StatCard, Table, Th, TwoLine, type Tone } from "./ui";

const STATE: Record<ProgressState, { label: string; tone: Tone }> = {
  done: { label: "Completed", tone: "success" },
  building: { label: "In development", tone: "warning" },
  waiting: { label: "Not started", tone: "secondary" },
};

/**
 * What has been built so far, laid out as the template's dashboard: figures across the
 * top, then one box per stage of the quotation. Item codes are the quotation's own, so the
 * page can be read next to the paper.
 */
export function Progress(): React.ReactElement {
  const [data, setData] = useState<ProgressData | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let live = true;
    api
      .progress()
      .then((result) => {
        if (live) setData(result);
      })
      .catch((problem: unknown) => {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not load development status");
      });
    return () => {
      live = false;
    };
  }, []);

  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!data) return <Loading />;

  const items = data.stages.flatMap((stage) => stage.items);
  const building = items.filter((item) => item.state === "building").length;
  const waiting = items.filter((item) => item.state === "waiting").length;
  const percent = Math.round((data.totals.done / data.totals.total) * 100);

  return (
    <>
      <PageHeader
        title="System development status"
        subtitle={`Development progress and items awaiting OHC input. Quotation ${data.quotation}, started ${data.startedOn}; item codes match the quotation.`}
      />

      {data.recentUpdates?.length ? (
        <Box title={`Latest updates${data.updatedOn ? ` (${data.updatedOn})` : ""}`}>
          <ul className="list-disc ps-5 mb-0 space-y-3 text-[0.813rem]">
            {data.recentUpdates.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </Box>
      ) : null}

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xxl:col-span-3 xl:col-span-3 md:col-span-6 col-span-12">
          <StatCard
            label="Items completed"
            value={`${data.totals.done} / ${data.totals.total}`}
            icon="ti ti-circle-check"
            tone="success"
            note={`${percent}% of the quotation`}
          />
        </div>
        <div className="xxl:col-span-3 xl:col-span-3 md:col-span-6 col-span-12">
          <StatCard label="In development" value={building} icon="ti ti-hammer" tone="warning" />
        </div>
        <div className="xxl:col-span-3 xl:col-span-3 md:col-span-6 col-span-12">
          <StatCard label="Not started" value={waiting} icon="ti ti-clock" tone="secondary" />
        </div>
        <div className="xxl:col-span-3 xl:col-span-3 md:col-span-6 col-span-12">
          <StatCard
            label="Awaiting OHC input"
            value={data.waitingOnYou.length}
            icon="ti ti-hand-stop"
            tone={data.waitingOnYou.length ? "danger" : "success"}
            note={data.waitingOnYou.length ? "See the list below" : "No input required"}
            {...(data.waitingOnYou.length ? { noteTone: "danger" as const } : {})}
          />
        </div>
      </div>

      <Box title="Overall progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[0.813rem] text-[#8c9097] dark:text-white/50">Completed so far</span>
          <span className="font-semibold">{percent}%</span>
        </div>
        <div className="progress progress-xs">
          <div
            className="progress-bar bg-primary"
            role="progressbar"
            style={{ width: `${Math.max(percent, 1)}%` }}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </Box>

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xxl:col-span-8 xl:col-span-8 col-span-12">
          {data.stages.map((stage) => {
            const done = stage.items.filter((item) => item.state === "done").length;
            return (
              <Box
                key={stage.key}
                title={stage.title}
                actions={
                  <Badge tone={done === stage.items.length ? "success" : "light"}>
                    {done} of {stage.items.length}
                  </Badge>
                }
              >
                <Table
                  head={
                    <>
                      <Th>Item code</Th>
                      <Th>Description</Th>
                      <Th end>Status</Th>
                    </>
                  }
                >
                  {stage.items.map((item) => (
                    <tr key={item.code} className="border-b border-defaultborder">
                      <td className="font-semibold">{item.code}</td>
                      <td className="!whitespace-normal">
                        <TwoLine main={item.label} sub={item.note} />
                      </td>
                      <td className="!text-end">
                        <Badge tone={STATE[item.state].tone}>{STATE[item.state].label}</Badge>
                      </td>
                    </tr>
                  ))}
                </Table>
              </Box>
            );
          })}
        </div>

        <div className="xxl:col-span-4 xl:col-span-4 col-span-12">
          <Box title="Awaiting OHC input">
            {data.waitingOnYou.length === 0 ? (
              <p className="text-[#8c9097] dark:text-white/50 text-[0.813rem] mb-0">No items are awaiting OHC input.</p>
            ) : (
              <ul className="list-none mb-0">
                {data.waitingOnYou.map((line, index) => (
                  <li key={line} className={index < data.waitingOnYou.length - 1 ? "mb-[0.9rem]" : ""}>
                    <div className="flex items-start">
                      <div className="me-2">
                        <span className="inline-flex items-center justify-center !w-[1.75rem] !h-[1.75rem] leading-[1.75rem] text-[0.75rem] rounded-full text-danger bg-danger/10 font-semibold">
                          {index + 1}
                        </span>
                      </div>
                      <p className="flex-grow text-[0.813rem] mb-0">{line}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Box>
          <Box title="About this page">
            <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-0">
              This page is part of the system and is updated with the same work it reports on. Nothing here
              is written manually afterwards.
            </p>
          </Box>
        </div>
      </div>
    </>
  );
}
